import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Role } from '@attendance/shared';
import type { FeatureKey } from '@attendance/shared/schemas/settings';
import { FeatureRoute } from './FeatureRoute.js';
import { PermissionGate } from '../common/PermissionGate.js';
import { RoleGate } from '../common/RoleGate.js';

/**
 * Route guard for a MANAGEMENT screen — the route enforces the same role list
 * its sidebar entry declares.
 *
 * Why this exists at all: `navItems.ts` gives most back-office items a `roles`
 * list, but that list only ever decided whether the SIDEBAR drew the link. The
 * routes themselves were open, so a MANAGER who typed /system/jobs — or kept a
 * bookmark from a previous role — got the page the nav had just told them they
 * could not have. `<RoleGate>` already existed and /reports/exec-overview
 * already used it exactly this way; this wrapper is that one route's shape,
 * made reusable so ~25 routes can adopt it without App.tsx turning into three
 * levels of nesting per line.
 *
 * This is CLIENT-side defence in depth, not data protection: the API scopes and
 * authorises every response on its own, and a determined caller can always talk
 * to the API directly. What the gate buys is that we never RENDER a management
 * screen to somebody who cannot use it — no half-empty tables, no buttons that
 * only 403, no "the menu says I'm not allowed but here's the page anyway".
 *
 * A route is gated by `permission` (a key from the catalog, or a list of which
 * holding ANY one suffices) wherever the matrix has a key for the page, and by
 * `roles` only where it has none. Copy whichever the matching nav entry carries.
 * Given both, both apply.
 *
 * Composition, not a new concept: `feature` (when given) is checked by the
 * existing `FeatureRoute` on the outside, exactly as the hand-written routes
 * nested them, then the role list by the existing `RoleGate` inside. Both keep
 * their current semantics for every existing caller.
 *
 * The redirect target is `/`, matching `FeatureRoute`. It has to be a route
 * EVERY authenticated user can hold: `/` is the personal workspace and is
 * ungated, so an under-privileged user lands on a page they can actually use.
 * `/overview` would be wrong — `AdminOverviewRoute` bounces it to `/` whenever
 * `hasAdminAccess` is false, so a MANAGER refused at /settings would bounce
 * twice, and a role with no management access at all would ping-pong.
 */
export function ManageRoute({
  roles,
  permission,
  feature,
  children,
}: {
  /** Copy the list from the matching nav entry in `navItems.ts` verbatim. */
  roles?: Role[];
  /** The nav entry's `permission`: what Settings → Roles & access grants. */
  permission?: string | readonly string[];
  feature?: FeatureKey | FeatureKey[];
  children: ReactNode;
}): JSX.Element {
  const to = <Navigate to="/" replace />;
  const byPermission = permission ? (
    <PermissionGate permission={permission} fallback={to}>
      {children}
    </PermissionGate>
  ) : (
    children
  );
  const gated = roles ? (
    <RoleGate allow={roles} fallback={to}>
      {byPermission}
    </RoleGate>
  ) : (
    byPermission
  );
  if (!feature) return <>{gated}</>;
  return <FeatureRoute feature={feature}>{gated}</FeatureRoute>;
}
